// Content Studio bundle fetch + t() resolver (docs/CMS_SCOPE.md §1, §2). C1 ships
// this infrastructure with nothing calling it yet — no hardcoded string is touched
// until the migration tranches begin in C2 (toasts/errors + onboarding copy). Until
// then this module is inert: loadContentBundle() has nothing to fetch (no version has
// ever been published), and every t() call anywhere that doesn't exist yet simply
// means the fallback always wins, which is exactly today's behaviour.
//
// Deliberately near-dependency-free — no imports from utils.js or constants.js — same
// reasoning as lib/oinks.js's own header comment: keeps this safe to import from
// anywhere without creating a cycle, and keeps APP_LOCALE's value duplicated here
// rather than shared, since a locale mismatch here only ever affects which bundle
// text is picked, never breaks anything (the fallback string is always still there).
//
// The ONE import: lib/buildFlags.js, itself dependency-free, which says which server a native
// build fetches the bundle from (Native Run A). Identity on the web.
import { apiUrl } from "./buildFlags.js";

const STORAGE_KEY_PREFIX = "lb-content-v";
const APP_LOCALE_FALLBACK = "en-GB"; // mirrors constants.js's APP_LOCALE
const EMPTY_BUNDLE = { schemaVersion: 1, version: 0, entries: [] };

let bundleCache = null; // { schemaVersion, version, entries: [...] } — in-memory for this session

// Guards every entry point to a bundle object against a malformed shape — from a
// corrupt localStorage value, a network response that isn't what /api/content
// promises, or literally anything else. This is the fix for a real incident: t()
// originally trusted `bundle.entries` was always an array and called .find() on it
// directly, which threw when that assumption broke, and since nothing caught it,
// React unmounted the ENTIRE app on any real (non-preview) screen — a blank white
// page for every visitor. t()'s whole documented contract is "never throws"; this is
// what actually makes that true rather than just asserting it.
function isValidBundle(candidate) {
  return !!candidate && typeof candidate === "object" && Array.isArray(candidate.entries);
}

function readCachedBundle() {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
        const parsed = JSON.parse(localStorage.getItem(key));
        return isValidBundle(parsed) ? parsed : null;
      }
    }
  } catch {
    // Corrupt or inaccessible localStorage — treat as "no cache," never throw.
  }
  return null;
}

function writeCachedBundle(bundle) {
  try {
    // Clear any older cached version before writing the new one, so a stale bundle
    // from a previous publish never lingers as dead weight in localStorage.
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_KEY_PREFIX)) localStorage.removeItem(key);
    }
    localStorage.setItem(`${STORAGE_KEY_PREFIX}${bundle.version}`, JSON.stringify(bundle));
  } catch {
    // Storage full or unavailable — the in-memory cache still works for this session.
  }
}

// Fetches the published content bundle. Network-first with cache fallback, matching
// the service worker's own strategy for this endpoint (docs/CMS_SCOPE.md §1) — a
// failed fetch (offline, or the very first load before the SW has cached anything)
// falls back to whatever's in localStorage from a previous session, and if there's
// nothing there either, falls back to an empty bundle, which makes every t() call
// resolve to its fallback string. This function never throws.
export async function loadContentBundle() {
  try {
    // LB-144: inside the CMS preview the app reads the DRAFT bundle instead of the published
    // one, so an editor sees unpublished work rendered in place. The flag is injected by
    // /cms/preview and is Access-gated; nothing sets it in a real session.
    const previewCfg = (typeof window !== "undefined" && window.__LB_CMS_PREVIEW__) || null;
    const contentUrl = previewCfg && previewCfg.draftUrl ? `${previewCfg.draftUrl}?platform=web` : apiUrl("/api/content?platform=web");
    const res = await fetch(contentUrl, { headers: { Accept: "application/json" } });
    if (res.ok) {
      const bundle = await res.json();
      if (isValidBundle(bundle)) {
        bundleCache = bundle;
        // A DRAFT bundle is never persisted: it would outlive the preview and become the
        // text a real session reads from cache. In-memory only.
        if (!previewCfg) writeCachedBundle(bundle);
        return bundle;
      }
      // Response parsed but wasn't shaped like a real bundle (e.g. an error body that
      // still came back with a 2xx status) — deliberately NOT cached, and fall through
      // to whatever's already known-good below rather than trusting it.
    }
  } catch {
    // Offline, network error, or a response body that wasn't valid JSON at all.
  }
  const cached = readCachedBundle();
  if (cached) {
    bundleCache = cached;
    return cached;
  }
  bundleCache = EMPTY_BUNDLE;
  return bundleCache;
}

function currentBundle() {
  if (isValidBundle(bundleCache)) return bundleCache;
  const cached = readCachedBundle();
  if (cached) return cached;
  return EMPTY_BUNDLE;
}

function interpolate(text, vars) {
  if (!vars) return text;
  return text.replace(/\{([a-zA-Z0-9_]+)\}/g, (match, name) => (name in vars ? String(vars[name]) : match));
}

// t(key, fallback, vars) — looks up `key` in the cached bundle, falls back to
// `fallback` (the literal string that used to live at the call site) if the bundle
// has no entry, is stale, or hasn't loaded yet. This is the ONLY function call sites
// should ever use to read CMS-managed copy (docs/CMS_SCOPE.md §2) — nothing here can
// return HTML or run code, only interpolated plain text, which is the injection
// defence: content is never a vector because it's never rendered as anything but text.
// Wrapped in try/catch as a last line of defence — currentBundle() and interpolate()
// are both defensive already, but this call sits directly in component render paths
// all over the app, and an uncaught throw here would unmount the whole tree. A CMS
// lookup going wrong should never be able to take the app down; worst case is the
// fallback string, exactly as if nothing had ever been migrated.

// ---------------------------------------------------------------------------------------
// LB-145, option B: the preview registry.
//
// The side-by-side CMS needs to know which element on screen came from which key. The
// explicit alternative (option A) spreads a data-k attribute at ~250 call sites and every
// future one. This records key -> RENDERED string as t() resolves it, POST-interpolation, so
// a string containing {count} is registered as the "3" the DOM actually holds. A walker in
// the preview then matches text nodes against the registry and sets data-k on the element.
//
// Why post-interpolation matters, recorded because I got it wrong once already: matching the
// PUBLISHED text instead cannot work for any string with a variable in it — the CMS row holds
// the placeholder, the DOM holds the value, and they never match.
//
// ARMED ONLY IN PREVIEW. Outside it, register() is a no-op costing one boolean test per
// resolve, and nothing is retained. check-preview-registry.mjs asserts that; a registry that
// filled up in a real session would be a slow leak on every household's device.
// ---------------------------------------------------------------------------------------
let previewRegistry = null; // Map<renderedString, Set<key>> while armed, else null

export function armPreviewRegistry() {
  if (!previewRegistry) previewRegistry = new Map();
  return previewRegistry;
}
export function isPreviewRegistryArmed() { return previewRegistry !== null; }
export function previewKeysFor(text) {
  if (!previewRegistry) return [];
  const hit = previewRegistry.get(String(text == null ? "" : text).trim());
  return hit ? [...hit] : [];
}
export function previewRegistrySnapshot() {
  if (!previewRegistry) return {};
  const out = {};
  for (const [text, keys] of previewRegistry) out[text] = [...keys];
  return out;
}
function register(key, rendered) {
  if (!previewRegistry) return;
  if (typeof rendered !== "string" || !rendered.trim()) return;
  const k = rendered.trim();
  if (!previewRegistry.has(k)) previewRegistry.set(k, new Set());
  previewRegistry.get(k).add(key);
}

export function t(key, fallback, vars) {
  try {
    const bundle = currentBundle();
    const entry = bundle.entries.find((e) => e && e.id === key);
    if (!entry) { const out = interpolate(fallback, vars); register(key, out); return out; }
    const text = entry.value && (entry.value[APP_LOCALE_FALLBACK] || Object.values(entry.value)[0]);
    if (typeof text !== "string" || !text) { const out = interpolate(fallback, vars); register(key, out); return out; }
    const out = interpolate(text, vars);
    register(key, out);
    return out;
  } catch {
    // Even the defensive path renders words a person can see, so it registers too.
    const out = interpolate(fallback, vars);
    try { register(key, out); } catch { /* registry must never take the app down either */ }
    return out;
  }
}

// ------------------------------ structured content (word lists, C3+) ------------------------------
// t() above is for single interpolated strings. Word lists, and future content types
// (maths params in C4, Money Quest in C5), are structured collections rather than one
// string per key — these two functions are the equivalent "read with safe fallback"
// primitive for that shape. Unlike t()'s per-key fallback, this falls back at the
// WHOLE-COLLECTION level: if the CMS has published ANY categories of the requested
// type, those are used exclusively (a parent managing lists in the CMS shouldn't see
// their own categories silently mixed with leftover code defaults); if it has none
// yet, the caller gets null and is expected to fall back to its own baked-in starter
// data. This mirrors docs/CMS_SCOPE.md §4.3: "the code constants become the baked
// fallback" — a collection-level switch, not a per-word merge.

// Returns CMS-published word-list categories of `type` ("spelling"|"vocab"), sorted by
// sortOrder, with retired ones excluded — or null if none are published yet, meaning
// "use the code-constant starter categories instead." Never throws.
export function cmsWordlistCategories(type) {
  try {
    const bundle = currentBundle();
    const cats = bundle.entries.filter(
      (e) => e && e.area === "wordlist-category" && e.value && typeof e.value === "object" && e.value.type === type && !e.value.retired
    );
    if (!cats.length) return null;
    return cats
      .map((e) => ({
        id: e.id,
        label: typeof e.value.label === "string" ? e.value.label : e.id,
        minAge: typeof e.value.minAge === "number" ? e.value.minAge : null,
        maxAge: typeof e.value.maxAge === "number" ? e.value.maxAge : null,
        sortOrder: typeof e.value.sortOrder === "number" ? e.value.sortOrder : 0,
        // Difficulty Ladder (docs/DIFFICULTY_LADDER_SCOPE.md): optional school-year
        // claim, sanitized at the consumer (lib/ladder.js). Absent on every entry
        // until the ladder import — age resolution unchanged until then.
        years: Array.isArray(e.value.years) ? e.value.years : [],
      }))
      .sort((a, b) => a.sortOrder - b.sortOrder);
  } catch {
    return null;
  }
}

// Returns the word entries (plain value objects, e.g. { categoryId, word } or
// { categoryId, word, definition, distractors }) for a given category id within a
// word-area ("spelling-word" | "vocab-word"), retired ones excluded — same
// convention as cmsWordlistCategories. Always an array, even for a category that
// legitimately has zero words yet — that's a different, valid state from "no CMS
// categories at all" (which is what cmsWordlistCategories signals with null). Never throws.
// A `config` entry's value (a JSON object of parameters read by code), or null. First use:
// adaptive.params (LB-19) — thresholds live in the CMS, not in a deploy.
export function cmsConfig(id) {
  try {
    const bundle = currentBundle();
    const e = bundle.entries.find((x) => x && x.area === "config" && x.id === id && x.value && typeof x.value === "object");
    return e ? e.value : null;
  } catch { return null; }
}

export function cmsWordsForCategory(wordArea, categoryId) {
  try {
    const bundle = currentBundle();
    return bundle.entries
      .filter((e) => e && e.area === wordArea && e.value && typeof e.value === "object" && e.value.categoryId === categoryId && !e.value.retired)
      .map((e) => e.value);
  } catch {
    return [];
  }
}

// Returns CMS-published maths bands, sorted by sortOrder, retired ones excluded — or
// null if none are published yet, meaning "use MATHS_BANDS in lib/maths.js instead."
// Same whole-collection fallback as cmsWordlistCategories. Note this only covers band
// METADATA (label/blurb/age range) — which questions a band actually generates is
// governed by cmsMathsTypeMix/cmsMathsTypeParams/cmsMathsSlotItems below. Never throws.
export function cmsMathsBands() {
  try {
    const bundle = currentBundle();
    const bands = bundle.entries.filter(
      (e) => e && e.area === "maths-band" && e.value && typeof e.value === "object" && !e.value.retired
    );
    if (!bands.length) return null;
    return bands
      .map((e) => ({
        id: e.id,
        label: typeof e.value.label === "string" ? e.value.label : e.id,
        blurb: typeof e.value.blurb === "string" ? e.value.blurb : "",
        minAge: typeof e.value.minAge === "number" ? e.value.minAge : null,
        maxAge: typeof e.value.maxAge === "number" ? e.value.maxAge : null,
        sortOrder: typeof e.value.sortOrder === "number" ? e.value.sortOrder : 0,
        // Difficulty Ladder (docs/DIFFICULTY_LADDER_SCOPE.md): optional school-year
        // claim, sanitized at the consumer (lib/ladder.js). Absent on every entry
        // until the ladder import — age resolution unchanged until then.
        years: Array.isArray(e.value.years) ? e.value.years : [],
      }))
      .sort((a, b) => a.sortOrder - b.sortOrder);
  } catch {
    return null;
  }
}

// ------------------------------ maths parameterization (docs/MATHS_PARAMETERIZATION_SCOPE.md) ------------------------------
// Deliberately PER-KEY fallback throughout, same reasoning as maths bands' per-band
// fallback (Alan, Aug 2026: "that's a weird design that's guaranteed to cause
// problems" — see effectiveMathsBands' header comment in lib/maths.js). A band's
// type-mix is looked up by band id; missing means "use that band's default mix,"
// not "fall back to nothing." A type's params/slot-items are looked up by type id;
// missing means "use that type's own hardcoded defaults." Publishing one band's
// weights, or one type's params, never affects any other band or type.

// Returns the qualitative weight map for a band ({ typeId: "high"|"medium"|"low"|"none" })
// or null if nothing is published for this specific band id. Never throws.
export function cmsMathsTypeMix(bandId) {
  try {
    const bundle = currentBundle();
    const entry = bundle.entries.find((e) => e && e.area === "maths-type-mix" && e.id === bandId);
    if (!entry || !entry.value || typeof entry.value !== "object" || !entry.value.weights || typeof entry.value.weights !== "object") return null;
    return entry.value.weights;
  } catch {
    return null;
  }
}

// Maths Ladder v2 (docs/MATHS_LADDER_V2_SPEC.md): the SAME type-mix entry may carry two
// more per-type maps beside `weights` — `grades` ({ typeId: 1..5 }, the type's grade INSIDE
// this band, replacing grade-by-position) and `l3` ({ typeId: factor }, how far level 3
// stretches the numbers; 1 = not at all). Both optional and per-key: a type not named
// keeps the registry default. Never throws.
export function cmsMathsTypeGrades(bandId) {
  try {
    const bundle = currentBundle();
    const entry = bundle.entries.find((e) => e && e.area === "maths-type-mix" && e.id === bandId);
    const g = entry && entry.value && entry.value.grades;
    return g && typeof g === "object" && !Array.isArray(g) ? g : null;
  } catch { return null; }
}
export function cmsMathsTypeL3(bandId) {
  try {
    const bundle = currentBundle();
    const entry = bundle.entries.find((e) => e && e.area === "maths-type-mix" && e.id === bandId);
    const f = entry && entry.value && entry.value.l3;
    return f && typeof f === "object" && !Array.isArray(f) ? f : null;
  } catch { return null; }
}

// Maths Ladder v2: params, levels and slot items are looked up BAND-FIRST. An entry whose id
// is "<bandId>:<typeId>" overrides that type for that band only; the plain "<typeId>" entry
// remains the global override for every band that has no band-scoped one; the registry
// default sits under both. This is what lets Challenger (Y4) and Navigator (Y5) share the
// `multiplication` type at different sizes — before this, one number range per type was the
// constraint that stopped the ladder's content from being tuned per year at all
// (docs/DIFFICULTY_LADDER_SCOPE.md §7 "Still deferred").
function findBandFirst(entries, area, typeId, bandId) {
  const scoped = bandId ? entries.find((e) => e && e.area === area && e.id === `${bandId}:${typeId}`) : null;
  return scoped || entries.find((e) => e && e.area === area && e.id === typeId) || null;
}

// Returns the params object for a single type, or null if nothing is published for
// this specific type id. Never throws.
// Per-type LEVEL overrides (LB-19 2d): the same maths-type-params entry may carry
// `levels: { "1": {...partial params}, "3": {...}, fixed?: true }`. Absent → derived by rule.
export function cmsMathsTypeLevels(typeId, bandId) {
  try {
    const bundle = currentBundle();
    const entry = findBandFirst(bundle.entries, "maths-type-params", typeId, bandId);
    return entry && entry.value && entry.value.levels && typeof entry.value.levels === "object" ? entry.value.levels : null;
  } catch { return null; }
}
export function cmsMathsTypeParams(typeId, bandId) {
  try {
    const bundle = currentBundle();
    const entry = findBandFirst(bundle.entries, "maths-type-params", typeId, bandId);
    if (!entry || !entry.value || typeof entry.value !== "object" || !entry.value.params || typeof entry.value.params !== "object") return null;
    return entry.value.params;
  } catch {
    return null;
  }
}

// Returns every published Help & FAQ entry as raw { id, value } pairs, or null if the
// CMS has none — meaning "use the baked corpus in lib/faq.js instead." Whole-collection
// fallback, same as cmsWordlistCategories and for the same reason: a half-CMS,
// half-hardcoded FAQ is how a stale answer survives an edit. Shape validation lives in
// lib/faq.js's normaliseEntry, not here, so this stays a dumb read. Never throws.
export function cmsFaqEntries() {
  try {
    const bundle = currentBundle();
    const rows = bundle.entries.filter((e) => e && e.area === "faq" && e.value && typeof e.value === "object");
    if (!rows.length) return null;
    return rows.map((e) => ({ id: e.id, value: e.value }));
  } catch {
    return null;
  }
}

// ------------------------------ starter sets (LB-131) ------------------------------
// The standard task and habit sets a child is seeded with (utils.seedStandardSet). Four
// entries in the `starter-set` area — tasks/habits × young/older, split at
// AGE_UP_THRESHOLD (LB-130) — each `{ kind, band, items: [{ label, weight? }] }`.
//
// NAMES AND WEIGHTS ONLY, NEVER PRICES. Every token amount is derived from the child's
// own cap at seed time (LB-132); a price here would sit outside the guardrails engine.
// The worker validator (validateStarterSetEntry) rejects price-shaped keys at save time,
// and this reader ignores anything but label/weight, so a price cannot arrive by either
// door.
//
// Fallback is per ENTRY, not per collection like cmsWordlistCategories: the four sets
// are independent (an operator retuning the older tasks has not said anything about the
// younger habits), so each missing or malformed entry falls back to its own baked
// constant on its own. Returns null for "use the baked set" — including for an entry
// that exists but has no usable items, which is the one CMS edit that must never reach
// a child (LB-131: an empty set is rejected at save, and ignored here as belt and braces).
// Never throws.
export function cmsStarterSet(kind, band) {
  try {
    const bundle = currentBundle();
    const entry = bundle.entries.find((e) => e && e.area === "starter-set" && e.id === `starter.${kind}.${band}`);
    const v = entry && entry.value && typeof entry.value === "object" ? entry.value : null;
    if (!v || v.retired || !Array.isArray(v.items)) return null;
    const items = v.items
      .filter((it) => it && typeof it === "object" && typeof it.label === "string" && it.label.trim())
      .map((it) => (kind === "tasks"
        ? { label: it.label.trim(), weight: typeof it.weight === "number" && it.weight > 0 ? it.weight : null }
        : { label: it.label.trim() }));
    if (!items.length) return null;
    // A task set is only usable if every task carries a positive weight — a partial set
    // of weights has no sensible reading, so the whole entry yields to the baked one.
    if (kind === "tasks" && items.some((it) => it.weight === null)) return null;
    return items;
  } catch {
    return null;
  }
}

// Returns the slot-items array ([{ id, label, weight, ...extra }]) for a single type,
// or null if nothing is published for this specific type id. Never throws.
export function cmsMathsSlotItems(typeId, bandId) {
  try {
    const bundle = currentBundle();
    const entry = findBandFirst(bundle.entries, "maths-slot-items", typeId, bandId);
    if (!entry || !entry.value || typeof entry.value !== "object" || !Array.isArray(entry.value.items) || !entry.value.items.length) return null;
    return entry.value.items;
  } catch {
    return null;
  }
}
